import {createAudioContext} from "./TextToSpeechUtilsPoly";
import {Queue} from "../contexts/Queue";

//AudioPlayer receives audio chunks and queues them for playback on a device
//Its whole purpose is to encapsulate logic related to the Web Audio API
export class AudioPlayer {
    private audioContext: AudioContext;
    private currentGainNode: GainNode;
    private audioDeviceId: string;
    private audioBufferSourceNode: AudioBufferSourceNode | null = null;
    private pendingSpeakerQueue: Queue<AudioBuffer>;
    private imSpeaking: boolean = false;
    private pollingInterval: NodeJS.Timer | undefined = undefined;
    private gain: number = 0.5
    private isStopped: boolean = false;
    private isSwitchingOutputDevice: boolean = false;

    constructor(deviceId: string, initialGain: number = 0.5) {
        this.audioDeviceId = deviceId;
        this.audioContext = createAudioContext(deviceId) as AudioContext;
        this.currentGainNode = this.audioContext.createGain();
        this.gain = initialGain;
        this.currentGainNode.gain.setValueAtTime(this.gain, this.audioContext.currentTime);
        this.pendingSpeakerQueue = new Queue<AudioBuffer>();
    }

    public start(): void {
        this.isStopped = false;
        if (this.pollingInterval) {
            throw new Error("[AudioPlayer] Already running.");
        }

        this.pollingInterval = setInterval(() => {
            if (this.imSpeaking) return;

            const audioToSpeak = this.pendingSpeakerQueue.dequeue();
            if (audioToSpeak) this.playOnDevice(audioToSpeak);
        }, 100);
    }

    public stop(): void {
        clearInterval(this.pollingInterval);
        this.pollingInterval = undefined;
        this.isStopped = true;

        try {
            if (this.audioBufferSourceNode) {
                this.audioBufferSourceNode.onended = null;
                this.audioBufferSourceNode.stop();
                this.audioBufferSourceNode.disconnect();
                this.audioBufferSourceNode = null;
            }
        } catch {
        }

        try {
            this.currentGainNode?.disconnect();
        } catch {
        }

        this.imSpeaking = false;
        try {
            this.audioContext.close();
        } catch {
        }
        console.info("AudioPlayer Stopped")
    }

    public play(rawAudioChunk: ArrayBuffer): void {
        console.info("[AudioPlayer] Playing... ", rawAudioChunk.byteLength);
        this.audioContext.decodeAudioData(rawAudioChunk)
            .then((audioBuffer) => this.pendingSpeakerQueue.enqueue(audioBuffer))
            .catch((error) => console.log('[Text2SpeechProcessor] err', error));
    }

    public playing(): boolean {
        return this.imSpeaking
    }

    public setVolume(gain: number): void {
        this.gain = gain;
        if (this.currentGainNode) {
            const effectiveGain = this.isSwitchingOutputDevice ? 0 : this.gain;
            this.currentGainNode.gain.setValueAtTime(effectiveGain, this.audioContext.currentTime);
        }
    }

    public setOutputDevice(deviceId: string, force: boolean = false): void {
        if (!deviceId || (deviceId === this.audioDeviceId && !force) || this.isStopped) {
            return;
        }

        this.isSwitchingOutputDevice = true;
        try {
            this.currentGainNode?.gain?.setValueAtTime(0, this.audioContext.currentTime);
        } catch {
        }

        const resumeContext = async () => {
            if (this.audioContext.state === 'suspended') {
                try {
                    await this.audioContext.resume();
                } catch {
                }
            }
        };

        const recreateContext = async () => {
            try {
                if (this.audioBufferSourceNode) {
                    this.audioBufferSourceNode.onended = null;
                    this.audioBufferSourceNode.stop();
                    this.audioBufferSourceNode.disconnect();
                    this.audioBufferSourceNode = null;
                }
            } catch {
            }

            try {
                this.currentGainNode?.disconnect();
            } catch {
            }

            try {
                this.audioContext?.close();
            } catch {
            }

            this.audioDeviceId = deviceId;
            this.audioContext = createAudioContext(deviceId) as AudioContext;
            this.currentGainNode = this.audioContext.createGain();
            this.currentGainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
            this.imSpeaking = false;
            await resumeContext();
        };

        const finishSwitch = () => {
            this.isSwitchingOutputDevice = false;
            try {
                this.currentGainNode.gain.setValueAtTime(this.gain, this.audioContext.currentTime);
            } catch {
            }
        };

        const contextWithSink = this.audioContext as AudioContext & {
            setSinkId?: (sinkId: string) => Promise<void>;
        };

        if (typeof contextWithSink.setSinkId === 'function') {
            contextWithSink.setSinkId(deviceId)
                .then(async () => {
                    this.audioDeviceId = deviceId;
                    await resumeContext();
                    finishSwitch();
                })
                .catch(async () => {
                    await recreateContext();
                    finishSwitch();
                });
            return;
        }

        void recreateContext().finally(() => finishSwitch());
    }

    // public getAudioSource() : AudioNode {
    //     return this.currentGainNode
    // }

    // public getAudioSource() { return this.sou }             // pre-gain
    public getPostGainNode():AudioNode { return this.currentGainNode }



    private playOnDevice(buffer: AudioBuffer): void {
        if (this.imSpeaking || this.isStopped || this.audioContext.state === 'closed') {
            return;
        }

        this.imSpeaking = true;

        this.currentGainNode.gain.setValueAtTime(this.gain, this.audioContext.currentTime);

        this.audioBufferSourceNode = this.audioContext.createBufferSource();
        this.audioBufferSourceNode.buffer = buffer;
        this.audioBufferSourceNode.onended = () => {
            try {
                this.audioBufferSourceNode?.disconnect();
            } catch {
            }
            this.audioBufferSourceNode = null;
            this.imSpeaking = false;
        };

        this.audioBufferSourceNode.connect(this.currentGainNode);
        this.currentGainNode.connect(this.audioContext.destination);
        this.audioBufferSourceNode.start();
    }
}